// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a component for displaying a chronological list of audit events
 * related to a specific organization.
 */
import {
  apiAuditEvents,
  crmAuditEvents,
  getOrganizationAuditEvents,
  organizationAuditEvents,
  organizationDeleteAuditEvents,
} from '@/audit/api/get-organization-audit-events';
import { AuditEventListItem } from '@/audit/components/audit-event-list-item';
import { Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';

interface Props {
  /**
   * The key of the organization whose audit events should be displayed.
   */
  organizationKey: string;
}

/**
 * Displays a vertical list of audit events for a specific organization.
 * This component suspends while fetching the audit events.
 *
 * Each audit event is displayed using the AuditEventListItem component,
 * which provides a consistent format for displaying event details.
 */
export function OrganizationAuditList(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['organization', props.organizationKey, 'audit'],
    queryFn: () =>
      getOrganizationAuditEvents(
        props.organizationKey,
        organizationAuditEvents
      ),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}

export function OrganizationDeleteAuditList(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['organization', props.organizationKey, 'audit', 'delete'],
    queryFn: () =>
      getOrganizationAuditEvents(
        props.organizationKey,
        organizationDeleteAuditEvents
      ),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}

export function OrganizationCRMAuditList(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['organization', props.organizationKey, 'audit', 'crm'],
    queryFn: () =>
      getOrganizationAuditEvents(props.organizationKey, crmAuditEvents),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}

export function OrganizationAPIKeyAuditList(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['organization', props.organizationKey, 'audit', 'api-key'],
    queryFn: () =>
      getOrganizationAuditEvents(props.organizationKey, apiAuditEvents),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}
